import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import * as authApi from '../api/auth';
import { setAccessToken } from '../lib/api';
import type { components } from '../types/api';
import { identifyUser, resetUser } from '../lib/telemetry';

const GUEST_FLAG = 'guestMode';

type User = components['schemas']['UserProfileResponse'];

interface AuthState {
  user: User | null;
  status: 'loading' | 'authenticated' | 'anonymous' | 'guest';
  login(email: string, password: string): Promise<void>;
  register(email: string, password: string, displayName: string): Promise<{ email: string; message: string }>;
  logout(): Promise<void>;
  continueAsGuest(): void;
  setUser(user: User): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
        if (!res.ok) { setStatus(localStorage.getItem(GUEST_FLAG) === '1' ? 'guest' : 'anonymous'); return; }
        const body = await res.json();
        setAccessToken(body.token);
        setUser(body.user);
        setStatus('authenticated');
        identifyUser(body.user.id, {
          displayName: body.user.displayName ?? '',
          abbreviation: body.user.abbreviation,
        });
      } catch {
        setStatus(localStorage.getItem(GUEST_FLAG) === '1' ? 'guest' : 'anonymous');
      }
    })();
  }, []);

  return (
    <AuthContext.Provider value={{
      user, status,
      async login(email, password) {
        const res = await authApi.login(email, password);
        localStorage.removeItem(GUEST_FLAG);
        setUser(res.user); setStatus('authenticated');
        identifyUser(res.user.id, {
          displayName: res.user.displayName ?? '',
          abbreviation: res.user.abbreviation,
        });
      },
      async register(email, password, displayName) {
        // Registration no longer auto-logs in. User must confirm email first.
        return authApi.register(email, password, displayName);
      },
      async logout() {
        await authApi.logout(); setUser(null); setStatus('anonymous');
        localStorage.removeItem(GUEST_FLAG);
        resetUser();
      },
      continueAsGuest() {
        localStorage.setItem(GUEST_FLAG, '1');
        setAccessToken(null);
        setUser(null);
        setStatus('guest');
      },
      setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
